import { render } from 'react-dom';
import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import styles from './Spinner.module.scss';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  full: boolean;
  visible: boolean;
  inline: boolean;
  size: 'large' | 'small' | 'normal';
}

enum Size {
  large = '60',
  normal = '40',
  small = '20',
}

export default function Spinner(props: Props) {
  const {
    className,
    full,
    visible,
    inline,
    size,
    color = '#007AFF',
    ...rest
  } = props;
  const idRef = useRef(Math.floor(Math.random() * 100000000));

  useEffect(() => {
    if (full === false) return;

    if (visible) {
      fullSpinnerControl.show();
      document.body.classList.add(styles.spinnerShow);
    }

    return () => {
      visible && fullSpinnerControl.hide();
      document.body.classList.remove(styles.spinnerShow);
    };
  }, [full, visible]);

  if (full) {
    return null;
  }

  return (
    <div
      {...rest}
      className={cn(className, styles.container, {
        [styles.inline]: inline,
        [styles.hidden]: !visible,
      })}
    >
      <svg
        className={styles.load}
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        width={Size[size]}
        height={Size[size]}
        viewBox="0 0 200 200"
      >
        <g>
          <linearGradient
            id={`rights${idRef.current}`}
            gradientUnits="userSpaceOnUse"
            x1="150"
            y1="20"
            x2="150"
            y2="180"
          >
            <stop offset="0" stopColor={color} />
            <stop offset="1" stopColor={color} />
          </linearGradient>
          <path
            fill={`url(#lefts${idRef.current})`}
            d="M100,0v20c44.1,0,80,35.9,80,80c0,44.1-35.9,80-80,80v20c55.2,0,100-44.8,100-100S155.2,0,100,0z"
          />
          <linearGradient
            id={`lefts${idRef.current}`}
            gradientUnits="userSpaceOnUse"
            x1="50"
            y1="0"
            x2="50"
            y2="180"
          >
            <stop offset="0" stopColor="#FFFFFF" />
            <stop offset="1" stopColor={color} />
          </linearGradient>
          <path
            fill={`url(#rights${idRef.current})`}
            d="M20,100c0-44.1,35.9-80,80-80V0C44.8,0,0,44.8,0,100s44.8,100,100,100v-20C55.9,180,20,144.1,20,100z"
          />
          <circle fill={color} cx="100" cy="10" r="10" />
        </g>
      </svg>
    </div>
  );
}

Spinner.defaultProps = {
  full: false,
  visible: true,
  inline: false,
  size: 'normal',
};

const fullSpinnerEl = document.createElement('div');
let showCount = 0;

fullSpinnerEl.className = cn(styles.container, styles.full);
fullSpinnerEl.style.display = 'none';
document.body.appendChild(fullSpinnerEl);

render(<Spinner visible />, fullSpinnerEl);

const fullSpinnerControl = {
  timeout: '' as any,
  show() {
    clearTimeout(this.timeout);
    showCount++;
    this.timeout = setTimeout(() => {
      fullSpinnerEl.style.display = '';
    }, 100);
  },
  hide() {
    clearTimeout(this.timeout);
    --showCount;
    this.timeout = setTimeout(() => {
      if (showCount <= 0) {
        fullSpinnerEl.style.display = 'none';
      }
    }, 20);
  },
};

Spinner.show = () => {
  fullSpinnerControl.show();
};

Spinner.hide = () => {
  fullSpinnerControl.hide();
};
